<!-- src/apps/imageToDescription/components/ImageDescriptionForm.vue -->
<template>
  <div class="container mt-4">
    <div class="row">
      <div class="col-12 col-lg-6 text-light">

        <form @submit.prevent="submitForm" class="image-description-form  p-3 main-form-container ">
          <!-- Error Alert -->
          <div
              v-if="error"
              class="alert alert-danger alert-dismissible fade show mb-4"
              role="alert"
          >
            {{ error }}
            <button
                type="button"
                class="btn-close"
                @click="error = ''"
                aria-label="Close"
            ></button>
          </div>

          <div class=" form-block p-3 mb-3">
          <!-- Drag & Drop Area -->
          <div
              class="drop-zone mb-4"
              :class="{ 'drag-over': isDragging }"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleDrop"
              @click="triggerFileInput"
          >
            <div v-if="!selectedFile" class="drop-zone-content">
              <font-awesome-icon icon="cloud-upload-alt" size="3x"></font-awesome-icon>
              <p class="mb-0">Drag and drop your image here or click to browse</p>
            </div>
            <div v-else class="selected-file">
              <img :src="previewUrl" alt="Selected image" class="preview-image"/>
              <button
                  type="button"
                  class="btn btn-sm btn-danger remove-image"
                  @click.stop="removeFile"
              >
                Remove
              </button>
            </div>
            <input
                type="file"
                ref="fileInput"
                class="d-none"
                accept="image/*"
                @change="handleFileSelect"
            />
          </div>

          <!-- Your Brand DNA Guide -->
          <div class="mb-4">
            <label for="brandDna" class="form-label">Your Brand DNA Guide </label>
            <textarea
                id="brandDna"
                v-model="brandDna"
                class="form-control"
                :maxlength="400"
                rows="4"
                placeholder="Describe your brand's DNA..."
            ></textarea>
            <small class=" text-light ">{{ brandDna.length }}/400 characters</small>
          </div>

          <!-- Language Selection -->
          <div class="mb-4">
            <label for="language" class="form-label">Target Language</label>
            <select id="language" v-model="selectedLanguage" class="form-select">
              <option v-for="lang in languages"
                      :key="lang.code"
                      :value="lang.code">
                {{ lang.label }}
              </option>
            </select>
          </div>

          <!-- Progress Section (shown when processing) -->
          <div v-if="isProcessing" class="processing-status mb-4">
            <div class="stage-info mb-2">

              <span class="stage-text">{{ currentStage || 'Initializing...' }}</span>
            </div>
            <div class="progress w-100" style="height: 20px;">
              <div
                  class="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  :style="{ width: `${progress}%` }"
                  :aria-valuenow="progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
              >
                {{ progress }}%
              </div>
            </div>
          </div>
          </div>
          <!-- Submit Button -->
          <button
              type="submit"
              class="btn btn-primary  action-button"
              :disabled="!selectedFile || isProcessing"
          >
            {{ isProcessing ? 'Processing...' : 'Generate Description' }}
            <font-awesome-icon :icon="['fas', 'sparkles']"></font-awesome-icon>
          </button>
        </form>
      </div>

      <div class="col-12 col-lg-6">

        <product-description-results
            :results="results"
            :is-empty="!results"
            v-if="!isProcessing"
        />
        <ad-rotator v-else>
        </ad-rotator>
      </div>
    </div>


  </div>
</template>

<script>
import {ref, onMounted, onUnmounted} from 'vue';
import {v4 as uuidv4} from 'uuid';
import webSocketService, {HTTP_ENDPOINT} from '@/services/ws';
import ProductDescriptionResults from "@/apps/imageToDescription/ProductDescriptionResult.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {rotateAd} from "@/shared/AdsRotator.vue";
import AdRotator from "@/shared/AdsRotator.vue";

export default {
  name: 'ImageDescriptionForm',
  components: {AdRotator, FontAwesomeIcon, ProductDescriptionResults},

  setup() {
    let lang = navigator.language;

    const fileInput = ref(null);
    const selectedFile = ref(null);
    const previewUrl = ref('');
    const brandDna = ref('');
    const selectedLanguage = ref('en');
    const isDragging = ref(false);
    const isProcessing = ref(false);
    const progress = ref(0);
    const description = ref('');
    const error = ref('');
    const currentStage = ref('');
    const results = ref(null);
    const languages =  ref([
      { code: "en", label: "English" },
      { code: "ar", label: "Arabic" },
      { code: "bn", label: "Bengali" },
      { code: "bg", label: "Bulgarian" },
      { code: "zh", label: "Chinese (Simplified)" },
      { code: "zh-TW", label: "Chinese (Traditional)" },
      { code: "cs", label: "Czech" },
      { code: "da", label: "Danish" },
      { code: "nl", label: "Dutch" },
      { code: "en", label: "English" },
      { code: "fi", label: "Finnish" },
      { code: "fr", label: "French" },
      { code: "de", label: "German" },
      { code: "el", label: "Greek" },
      { code: "gu", label: "Gujarati" },
      { code: "hi", label: "Hindi" },
      { code: "hu", label: "Hungarian" },
      { code: "id", label: "Indonesian" },
      { code: "it", label: "Italian" },
      { code: "ja", label: "Japanese" },
      { code: "kn", label: "Kannada" },
      { code: "ko", label: "Korean" },
      { code: "ms", label: "Malay" },
      { code: "ml", label: "Malayalam" },
      { code: "mr", label: "Marathi" },
      { code: "no", label: "Norwegian" },
      { code: "pl", label: "Polish" },
      { code: "pt", label: "Portuguese" },
      { code: "pa", label: "Punjabi" },
      { code: "ro", label: "Romanian" },
      { code: "sk", label: "Slovak" },
      { code: "es", label: "Spanish" },
      { code: "sv", label: "Swedish" },
      { code: "ta", label: "Tamil" },
      { code: "te", label: "Telugu" },
      { code: "th", label: "Thai" },
      { code: "tr", label: "Turkish" },
      { code: "uk", label: "Ukrainian" },
      { code: "ur", label: "Urdu" },
      { code: "vi", label: "Vietnamese" }
    ]);


    onMounted(async () => {
      const browserLang = navigator.language;
      const browserLangCode = browserLang.split('-')[0];

      selectedLanguage.value = languages.value.some(lang => lang.code === browserLangCode)
          ? browserLangCode
          : 'en';

      try {
        const connected = await webSocketService.connect();
        if (!connected) {
          error.value = 'Unable to establish connection to server';
        }
      } catch (e) {
        error.value = 'Connection error: ' + e.message;
      }
    });

    onUnmounted(() => {
      webSocketService.disconnect();
    });

    const handleFileSelect = (event) => {
      const file = event.target.files[0];
      if (file) {
        processSelectedFile(file);
      }
    };

    const handleDrop = (event) => {
      isDragging.value = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        processSelectedFile(file);
      }
    };

    const processSelectedFile = (file) => {
      selectedFile.value = file;
      previewUrl.value = URL.createObjectURL(file);
    };

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const resetState = () => {
      isProcessing.value = false;
      progress.value = 0;
      error.value = '';
      results.value = null;
      currentStage.value = '';
    };

    const removeFile = () => {
      selectedFile.value = null;
      previewUrl.value = '';
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };

    const submitForm = async () => {
      rotateAd();
      if (!selectedFile.value) return;
      resetState();
      const clientTaskId = uuidv4();
      isProcessing.value = true;
      progress.value = 0;
      description.value = '';

      // Subscribe to WebSocket updates
      webSocketService.subscribe(clientTaskId, handleWebSocketMessage);

      try {
        const formData = new FormData();
        formData.append('file', selectedFile.value);
        formData.append('tab_id', webSocketService.tabId);
        formData.append('client_task_id', clientTaskId);
        formData.append('brand_dna', brandDna.value);
        formData.append('lang_code', selectedLanguage.value);

        await fetch(`${HTTP_ENDPOINT}/jobs/marketing/describe-image`, {
          method: 'POST',
          body: formData,
        });
      } catch (err) {
        error.value = 'Failed to submit image: ' + err.message;
        console.error('Error submitting form:', err);
        isProcessing.value = false;
      }
    };

    const handleWebSocketMessage = (data) => {
      console.log(data);
      if (data.type === 'result') {
        if (data.progress !== undefined) {
          progress.value = data.progress;
        }
        if (data.result !== undefined) {
          description.value = data.result;
          isProcessing.value = false;
        }
        // Reset processing state
        isProcessing.value = false;
        currentStage.value = '';
        progress.value = 0;

      }

      if (data.type === 'progress') {
        let progressInfo = data.progress;
        if (progressInfo.stage !== undefined) {
          currentStage.value = progressInfo.stage;
        }

        if (progressInfo.progress !== undefined) {
          progress.value = Math.round(progressInfo.progress);
        }


        if (progressInfo.title || progressInfo.content || progressInfo.usage || progressInfo.hashtags || progressInfo.keywords) {
          results.value = {
            ...results.value || {},
            title: progressInfo.title || results.value?.title,
            content: progressInfo.content || results.value?.content,
            usage: progressInfo.usage || results.value?.usage,
            hashtags: progressInfo.hashtags || results.value?.hashtags,
            keywords: progressInfo.keywords || results.value?.keywords
          };
        }
      }
      if (data.type === 'error') {
        error.value = data.error;
        isProcessing.value = false;
        description.value = '';
      }
    };

    return {
      error,
      fileInput,
      selectedFile,
      previewUrl,
      brandDna,
      selectedLanguage,
      isDragging,
      isProcessing,
      progress,
      description,
      handleFileSelect,
      handleDrop,
      triggerFileInput,
      removeFile,
      submitForm,
      currentStage,
      results,
      languages
    };
  },
};
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 4px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.drop-zone:hover {
  border-color: #666;
}

.drag-over {
  border-color: #2196f3;
  background-color: rgba(33, 150, 243, 0.1);
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
}

.selected-file {
  position: relative;
  display: inline-block;
}

.remove-image {
  position: absolute;
  top: 10px;
  right: 10px;
}

.description-content {
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #f8f9fa;
}
</style>
